/**
 * TransactionInsightsCard — [T61P0]
 *
 * A shared insights widget used on both the Dashboard ("dashboard" variant) and
 * the Transactions page ("full" variant).
 *
 * Dashboard variant:
 *   - Compact, no period selector
 *   - Computes directly from the `transactions` array passed in (recent transactions from Redux)
 *   - Tabs: By Type · By Category · By Account
 *
 * Full variant:
 *   - Period quick-selector (7d / 30d / 3m / 6m / 1y / All)
 *   - Fetches aggregated data from GET /transactions/summary with dateFrom/dateTo
 *   - Tabs: By Type · By Category · By Account · Recurring
 */

import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  buildInsightsByType,
  buildInsightsByAccount,
  buildRecurringBreakdown,
  buildCategoryTotals,
  type InsightByType,
  type InsightByAccount,
  type RecurringBreakdown,
  type CategoryTotalItem,
} from '@/lib/analytics';
import type { IWidget, Transaction, Account, Category } from '@/lib/types';
// [T-TRENDS-V2-WIRE] The full Transactions surface uses the authoritative V2
// adapter/skin pair; the dashboard keeps its compact array-backed Trends adapter.
import { TrendsAdapter } from './TrendsAdapter';
import { TrendsV2Adapter, type TrendsV2ResolvedState } from './TrendsV2Adapter';
import { TrendsV2Skin } from './TrendsV2Skin';
// [T246-MIGRATION-4] Delegate `SpendingHeatmapTab` to the canonical widget skin.
// [T270-PY] DayOfWeek redesigned skin (mount site: ./DayOfWeekRedesignSkin).
// `DayOfWeekBarSkin` is retained for `/widgets/insights` registry parity — only the
// Transaction Insights mount routes through the redesign.
import { DayOfWeekRedesignSkin, type DayOfWeekDrillInput } from './DayOfWeekRedesignSkin';
// [T270-PY] Canonical URL serializer for drill-through hrefs.
import { DEFAULT_FILTER_PERIOD, filtersToQueryString, type FilterState } from '@/lib/filters';
import { isFeatureEnabled } from '@/lib/featureFlags';
import SpendingHeatmapCalendarSkin from '@/components/dashboard/widgets/skins/insights/SpendingHeatmapCalendarSkin';
import type { HeatmapPayload, HeatmapDay } from '@/lib/insightsContracts';
import TxByTypeDonutSkin from '@/components/dashboard/widgets/skins/insights/TxByTypeDonutSkin';
import TxByCategoryBarSkin from '@/components/dashboard/widgets/skins/insights/TxByCategoryBarSkin';
import TxByAccountBarSkin from '@/components/dashboard/widgets/skins/insights/TxByAccountBarSkin';
import type { FacetBreakdownPayload } from '@/lib/insightsContracts';
// [T246-MIGRATION-5] Day-of-week bar — `day-of-week-bar` adapter.
import type { DayOfWeekPayload } from '@/lib/insightsContracts';
// [T246-MIGRATION-6] Top merchants bar — `top-merchants-bar` adapter.
import TopMerchantsBarSkin from '@/components/dashboard/widgets/skins/insights/TopMerchantsBarSkin';
import type { TopMerchantsPayload } from '@/lib/insightsContracts';
// [T246-MIGRATION-7] Amount histogram bar — `amount-histogram-bar` adapter.
import AmountHistogramBarSkin from '@/components/dashboard/widgets/skins/insights/AmountHistogramBarSkin';
import type { AmountHistogramPayload } from '@/lib/insightsContracts';
// [T246-MIGRATION-8] Cashflow composed — `cashflow-composed` adapter.
import CashflowComposedSkin from '@/components/dashboard/widgets/skins/CashflowComposedSkin';
import type { NetCashflowPayload } from '@/lib/insightsContracts';
// [T246-MIGRATION-9] Recurring vs discretionary — `recurring-vs-discretionary` adapter.
import RecurringVsDiscretionarySkin from '@/components/dashboard/widgets/skins/insights/RecurringVsDiscretionarySkin';
import type { RecurringVsDiscretionaryPayload } from '@/lib/insightsContracts';
import { useCurrency } from '@/hooks/useCurrency';
import { useWidgetSurfacePreferences } from '@/hooks/useWidgetSurfacePreferences';
// [T262-PY] Quick-curation modal opened from the Customize trigger on the Insights card.
import { CustomizeInsightsDialog } from '@/components/dashboard/insights-manager/CustomizeInsightsDialog';
import { Link } from 'react-router-dom';
import { getDateRangeFromFilter } from '@/components/transactions/UnifiedFilterBar';
import { useAppSelector, useAppDispatch } from '@/features/hooks';
import { updateProfile } from '@/features/auth/authSlice';
import api, { endpoints } from '@/services/api';
import {
  TrendingUp,
  BarChart3,
  RefreshCw,
  AlertCircle,
  SlidersHorizontal,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TransactionInsightsCardProps {
  variant: 'dashboard' | 'full';
  transactions: Transaction[];
  accounts: Account[];
  categories: Category[];
  // [T86P0] Full filter state from UnifiedFilterBar (full variant only)
  filters?: FilterState;
  onCalendarDayFilter?: (date: string, returnFocus: () => void) => void;
}

// Internal normalised data shape used for all chart tabs
interface InsightsState {
  byType: InsightByType[];
  byCategory: CategoryTotalItem[];
  byAccount: InsightByAccount[];
  recurring: RecurringBreakdown | null;
  totalCount: number;
}

const EMPTY_INSIGHTS: InsightsState = {
  byType: [],
  byCategory: [],
  byAccount: [],
  recurring: null,
  totalCount: 0,
};

// ─────────────────────────────────────────────────────────────────────────────
// Panel ID system for customizable layout [T70P2-d, T83P2]
// ─────────────────────────────────────────────────────────────────────────────

export type PanelId =
  | 'trends'
  | 'byType'
  | 'breakdown'
  | 'recurring'
  | 'heatmap'
  | 'dayOfWeek'
  | 'merchants'
  | 'histogram'
  | 'cashFlow';

const PANEL_LABELS: Record<PanelId, string> = {
  trends: 'Trends',
  byType: 'By Type',
  breakdown: 'Breakdown',
  recurring: 'Recurring',
  heatmap: 'Calendar',
  dayOfWeek: 'Day of Week',
  merchants: 'Merchants',
  histogram: 'Distribution',
  cashFlow: 'Cash Flow',
};

// [T246.5-FE-WIRE] Local mirror of the canonical 9-widget default order.
// Single source of truth lives in
// `backend/src/services/dashboardTemplates/insights-mirror.ts:53` -- the BE
// handler imports the same list to seed the `GET /widgets` response and
// `preferencesHandler.ts` re-exports it for the migration back-fill. Mirrored
// here so the FE can derive the "effective panel order" before the first
// network round-trip and so the dashboard variant has a deterministic
// fallback when the user's `enabled` list is empty (it is never empty in
// practice -- the BE validator rejects an empty PATCH -- but the defensive
// fallback keeps the UI renderable on the cold-cache first paint).
const INSIGHTS_MIRROR_DEFAULT_ORDER: readonly string[] = [
  'spending-trend',
  'analytics-insights-txn-type-breakdown',
  'analytics-category-breakdown',
  'analytics-insights-day-of-week-spending',
  'analytics-insights-spending-heatmap',
  'analytics-insights-recurring-vs-oneoff',
  'analytics-insights-amount-histogram',
  'analytics-daily-cashflow',
  'analytics-top-merchants',
];

// [T246.5-FE-WIRE] Map each `PanelId` (the FE-side tab identifier) to the
// canonical widget kind string the `/widgets` endpoint speaks. The mapping
// preserves the canonical order above when the user has not curated the
// surface yet -- the `enabled` array is intersected with the default order
// to derive the "effective" tab list.
const PANEL_ID_TO_WIDGET_KIND: Record<PanelId, string> = {
  trends: 'spending-trend',
  byType: 'analytics-insights-txn-type-breakdown',
  breakdown: 'analytics-category-breakdown',
  recurring: 'analytics-insights-recurring-vs-oneoff',
  heatmap: 'analytics-insights-spending-heatmap',
  dayOfWeek: 'analytics-insights-day-of-week-spending',
  merchants: 'analytics-top-merchants',
  histogram: 'analytics-insights-amount-histogram',
  cashFlow: 'analytics-daily-cashflow',
};

// [T246.5-FE-WIRE] Dashboard variant only renders a subset of the 9 panels
// (the "primary" 3: trends, byType, breakdown). Mirrors the legacy
// `DASHBOARD_CAPABLE_PANELS` set; the values are `PanelId` strings, not
// widget kinds, because the dashboard tab list is the PanelId-based view.
const DASHBOARD_PANEL_IDS: ReadonlySet<PanelId> = new Set<PanelId>([
  'trends', 'byType', 'breakdown',
]);

// [T246-MIGRATION-4] Local `interface HeatmapDay` removed — replaced by
// `HeatmapDay` imported from `@/lib/insightsContracts` (see top of file).
interface DayOfWeekData { day: string; total: number; count: number; }
// [T263-PY] Per-day category breakdown from GET /transactions/analytics/dayofweek?byCategory=true
interface DayOfWeekCategoryData {
  day: string;
  total: number;
  count: number;
  categories: Array<{ categoryId: string | null; name: string; emoji: string; total: number; count: number }>;
}
interface MerchantRow { name: string; count: number; total: number; trend: 'up' | 'down' | 'neutral'; trendPct: number; }
interface HistogramBucket { label: string; min: number; max: number | null; count: number; }

// [T83P2-b] Net cash flow data point returned by GET /transactions/analytics/net-cashflow
interface NetCashflowPoint { label: string; income: number; expenses: number; net: number; }

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Raw aggregated summary response from GET /transactions/summary */
interface RawInsightsByType {
  _id: string;
  total: number;
  count: number;
}
interface RawInsightsByCategory {
  total: number;
  count: number;
  category?: { name?: string; color?: string };
}
interface RawInsightsByAccount {
  total: number;
  count: number;
  account?: { id?: string; name?: string };
}
interface RawInsightsApiResponse {
  overview?: { transactionCount?: number };
  byType?: RawInsightsByType[];
  byCategory?: RawInsightsByCategory[];
  byAccount?: RawInsightsByAccount[];
}

/** Normalise server-side summary response → InsightsState. */
function normaliseApiInsights(data: RawInsightsApiResponse, accounts: Account[], categories: Category[]): InsightsState {
  // By Type
  const TYPE_COLORS: Record<string, string> = {
    income: 'hsl(var(--chart-2))',
    expense: 'hsl(var(--chart-1))',
    transfer: 'hsl(var(--chart-3))',
  };
  const totalCount: number = data.overview?.transactionCount ?? 0;

  const byType: InsightByType[] = (data.byType ?? []).map((item: RawInsightsByType) => ({
    type: item._id as InsightByType['type'],
    total: Math.abs(item.total ?? 0),
    count: item.count ?? 0,
    percentage: totalCount > 0
      ? Number(((item.count / totalCount) * 100).toFixed(1))
      : 0,
    color: TYPE_COLORS[item._id] ?? 'hsl(var(--chart-4))',
  }));

  // By Category — cross-reference categories for color/icon
  const categoryMap = new Map(categories.map((c) => [c.name, c]));
  const byCategory: CategoryTotalItem[] = (data.byCategory ?? [])
    .filter((item: RawInsightsByCategory) => item.total > 0)
    .slice(0, 7)
    .map((item: RawInsightsByCategory, index: number) => {
      const name = item.category?.name ?? 'Uncategorized';
      const localCat = categoryMap.get(name);
      return {
        category: name,
        total: Number(Math.abs(item.total).toFixed(2)),
        count: item.count ?? 0,
        color:
          localCat?.color ??
          item.category?.color ??
          `hsl(var(--chart-${(index % 5) + 1}))`,
      };
    });

  // By Account
  const accountMap = new Map(accounts.map((a) => [a.id, a]));
  const byAccount: InsightByAccount[] = (data.byAccount ?? []).map(
    (item: RawInsightsByAccount, index: number) => {
      const accId = String(item.account?.id ?? '');
      const localAcc = accountMap.get(accId);
      return {
        accountId: accId,
        accountName: localAcc?.name ?? item.account?.name ?? 'Unknown',
        total: Number((item.total ?? 0).toFixed(2)),
        count: item.count ?? 0,
        color: localAcc?.color ?? `hsl(var(--chart-${(index % 5) + 1}))`,
      };
    }
  );

  return { byType, byCategory, byAccount, recurring: null, totalCount };
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────────────
// [T89P2] Breakdown tab — unified Category / Account / Category×Account
// ─────────────────────────────────────────────────────────────────────────────

type BreakdownMode = 'category' | 'account' | 'categoryAccount';
const BREAKDOWN_STORAGE_KEY = 'fintrack:insights:breakdown-mode';

function getStoredBreakdownMode(): BreakdownMode {
  try {
    const stored = localStorage.getItem(BREAKDOWN_STORAGE_KEY);
    if (stored === 'category' || stored === 'account' || stored === 'categoryAccount') return stored;
  } catch { /* ignore */ }
  return 'category';
}

interface CategoryAccountItem {
  category: string;
  categoryColor: string;
  total: number;
  accounts: { name: string; total: number; color: string }[];
}

/** Nested list showing category → account two-level breakdown. */
function CategoryAccountView({ data }: { data: CategoryAccountItem[] }) {
  const { fmt } = useCurrency();

  if (!data.length) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        No data available for the selected period.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {data.map((cat) => (
        <div key={cat.category} className="rounded-lg border p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: cat.categoryColor }}
              />
              <span className="text-sm font-medium truncate">{cat.category}</span>
            </div>
            <span className="text-sm font-semibold tabular-nums shrink-0">{fmt(cat.total)}</span>
          </div>
          {/* Stacked progress bar */}
          <div className="h-2 w-full rounded-full bg-muted overflow-hidden flex">
            {cat.accounts.map((acc) => (
              <div
                key={acc.name}
                className="h-full"
                style={{
                  width: `${(acc.total / cat.total) * 100}%`,
                  backgroundColor: acc.color,
                }}
              />
            ))}
          </div>
          {/* Account sub-items */}
          <div className="space-y-1 pl-4">
            {cat.accounts.map((acc) => (
              <div key={acc.name} className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span
                    className="h-1.5 w-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: acc.color }}
                  />
                  <span className="text-muted-foreground truncate">{acc.name}</span>
                </div>
                <span className="font-medium tabular-nums shrink-0">{fmt(acc.total)}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** [T246-MIGRATION-2] BreakdownTab — delegates category + account modes
 *  to the canonical `tx-by-category-bar` and `tx-by-account-bar` widget
 *  skins via the SkinProps adapter pattern (mirrors MIGRATION-1's
 *  ByTypeTab → TxByTypeDonutSkin). The third mode (`categoryAccount`)
 *  still uses the bespoke `CategoryAccountView` until T246-NEW-1
 *  ships the `txn-category-account-cross` widget skin.
 *
 *  Preserves the E2E contract: `insights-breakdown-tab`,
 *  `breakdown-mode-toggle`, `breakdown-mode-{category,account,categoryAccount}`,
 *  `insights-category-tab`, `insights-account-tab`.
 */
function BreakdownTab({
  categoryData,
  accountData,
  categoryAccountData,
}: {
  categoryData: { label: string; total: number; count: number; color: string }[];
  accountData: { label: string; total: number; count: number; color: string }[];
  categoryAccountData: CategoryAccountItem[];
}) {
  const [mode, setMode] = useState<BreakdownMode>(getStoredBreakdownMode);

  useEffect(() => {
    try { localStorage.setItem(BREAKDOWN_STORAGE_KEY, mode); } catch { /* ignore */ }
  }, [mode]);

  // [T246-MIGRATION-2] Synthetic descriptor for the tx-by-category-bar skin.
  const categoryDescriptor: IWidget = useMemo(
    () => ({
      id: 'insights-breakdown-category',
      type: 'bar',
      kind: 'tx-by-category-bar',
      title: 'Spending by Category',
      metric: 'txByCategory',
      x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
      dataSource: 'analytics',
      config: { groupBy: 'category' },
    }),
    [],
  );

  // [T246-MIGRATION-2] Synthetic descriptor for the tx-by-account-bar skin.
  const accountDescriptor: IWidget = useMemo(
    () => ({
      id: 'insights-breakdown-account',
      type: 'bar',
      kind: 'tx-by-account-bar',
      title: 'Transactions by Account',
      metric: 'txByAccount',
      x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
      dataSource: 'analytics',
      config: { groupBy: 'account' },
    }),
    [],
  );

  // [T246-MIGRATION-2] Coerce local CategoryTotalItem[] → canonical FacetBreakdownPayload.
  const categoryPayload: FacetBreakdownPayload = useMemo(() => {
    const total = categoryData.reduce((acc, c) => acc + c.total, 0);
    const denom = total || 1;
    return {
      slices: categoryData.map((c) => ({
        id: c.label,
        name: c.label,
        amount: c.total,
        count: c.count,
        percentage: (c.total / denom) * 100,
      })),
      total,
      groupBy: 'category',
    };
  }, [categoryData]);

  // [T246-MIGRATION-2] Coerce local InsightByAccount[] → canonical FacetBreakdownPayload.
  const accountPayload: FacetBreakdownPayload = useMemo(() => {
    const total = accountData.reduce((acc, c) => acc + c.total, 0);
    const denom = total || 1;
    return {
      slices: accountData.map((c) => ({
        id: c.label,
        name: c.label,
        amount: c.total,
        count: c.count,
        percentage: (c.total / denom) * 100,
      })),
      total,
      groupBy: 'account',
    };
  }, [accountData]);

  return (
    <div data-testid="insights-breakdown-tab" className="space-y-4">
      {/* Segmented control — preserved E2E contract */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit" data-testid="breakdown-mode-toggle">
        {([
          ['category', 'Category'],
          ['account', 'Account'],
          ['categoryAccount', 'Category × Account'],
        ] as [BreakdownMode, string][]).map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setMode(value)}
            className={cn(
              'px-3 py-1.5 rounded-md text-xs font-medium transition-colors min-h-[36px]',
              mode === value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
            data-testid={`breakdown-mode-${value}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Category mode — delegates to TxByCategoryBarSkin */}
      {mode === 'category' && (
        <div data-testid="insights-category-tab" className="overflow-hidden min-w-0">
          <TxByCategoryBarSkin
            widget={categoryDescriptor}
            kind="tx-by-category-bar"
            data={categoryPayload}
            loading={false}
          />
        </div>
      )}

      {/* Account mode — delegates to TxByAccountBarSkin */}
      {mode === 'account' && (
        <div data-testid="insights-account-tab" className="overflow-hidden min-w-0">
          <TxByAccountBarSkin
            widget={accountDescriptor}
            kind="tx-by-account-bar"
            data={accountPayload}
            loading={false}
          />
        </div>
      )}

      {/* Category × Account mode — bespoke view until T246-NEW-1 ships the dedicated skin */}
      {mode === 'categoryAccount' && (
        <CategoryAccountView data={categoryAccountData} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

/** [T86P0] Build query params from full FilterState for analytics API calls.
 *  [T125-F1-j] Accepts timezone to pass to API and compute timezone-aware date ranges. */
function buildAnalyticsParams(filters?: FilterState, timezone?: string): Record<string, string> {
  if (!filters) return {};
  const params: Record<string, string> = {};
  const dateRange = getDateRangeFromFilter(filters.period, filters.dateFrom, filters.dateTo, timezone);
  if (dateRange.dateFrom) params.dateFrom = dateRange.dateFrom;
  if (dateRange.dateTo) params.dateTo = dateRange.dateTo;
  if (filters.type) params.type = filters.type;
  if (filters.accountIds?.[0]) params.accountId = filters.accountIds[0];
  if (filters.categoryIds?.[0]) params.categoryId = filters.categoryIds[0];
  if (filters.search) params.search = filters.search;
  if (filters.amountMin != null) params.amountMin = String(filters.amountMin);
  if (filters.amountMax != null) params.amountMax = String(filters.amountMax);
  if (filters.tags?.length) params.tags = filters.tags.join(',');
  if (filters.isRecurring) params.isRecurring = 'true';
  if (filters.status) params.status = filters.status;
  if (timezone) params.timezone = timezone;
  return params;
}

export function TransactionInsightsCard({
  variant,
  transactions,
  accounts,
  categories,
  filters,
  onCalendarDayFilter,
}: TransactionInsightsCardProps) {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.user);
  const { timezone } = useTimezone();
  const activeFilters = useMemo<FilterState>(
    () => filters ?? { period: DEFAULT_FILTER_PERIOD },
    [filters],
  );
  const renderTrendsV2Payload = useCallback(
    (state: TrendsV2ResolvedState) => <TrendsV2Skin state={state} />,
    [],
  );

  // [T70P2-b] Active tab -- driven by the still-active `insightsGroupBy`
  // Redux preference. The follow-up `useEffect` below keeps the value
  // in sync with the current `visiblePanels` so the URL/active-tab
  // doesn't dangle when the user removes the last enabled kind in
  // /widgets/insights.
  const [activeTab, setActiveTab] = useState<string>(() =>
    user?.preferences?.insightsGroupBy ?? 'breakdown'
  );
  const tabsListRef = useRef<HTMLDivElement>(null);
  const resetTabStrip = useCallback(() => {
    if (tabsListRef.current) tabsListRef.current.scrollLeft = 0;
  }, []);
  useEffect(() => {
    if (activeTab !== 'breakdown') return;
    const frame = requestAnimationFrame(resetTabStrip);
    return () => cancelAnimationFrame(frame);
  }, [activeTab, resetTabStrip]);
  // [T262-PY] Customize Insights modal open state — controlled by the right-side
  // trigger button on the card header. Opens the quick-curation dialog so users
  // can toggle individual widgets without leaving the Transactions page.
  const [customizeOpen, setCustomizeOpen] = useState<boolean>(false);

  // [T246.5-FE-WIRE] Wire the Wave 5 TanStack Query hook
  // (`useWidgetSurfacePreferences`, T246.5-FE-HOOKS). The hook returns a
  // `WidgetSurfacePreferenceEntry` (or undefined during the initial
  // network round-trip / cache cold-start). We fall back to the canonical
  // mirror default so the UI has a deterministic view BEFORE the GET
  // resolves -- the BE migration is idempotent so the defaults are
  // guaranteed non-empty on every surface.
  const { preferences } = useWidgetSurfacePreferences('transactions');

  // [T246.5-FE-WIRE] AC4 -- debounce the preference reads by 120 ms to
  // absorb rapid drag-reorder bursts emitted by /widgets/insights.
  // Without this debounce, the Transactions page would visibly flicker
  // on every intermediate reorder step. The 120 ms floor is asymmetric
  // on purpose vs. the 600 ms write-debounce: reads (which drive
  // layout) need a tighter floor than writes (which only hit the BE).
  const [debouncedEnabled, setDebouncedEnabled] = useState<string[] | null>(null);
  const readDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // [T246.5-FE-WIRE] Treat an empty `enabled` array the same as no
  // preferences at all (cold cache + user whose stored `enabled` is `[]`).
  // `??` only falls back on null/undefined, so an empty array would otherwise
  // bypass the canonical-default fallback and trigger the empty-state CTA.
  // Mirrors InsightsGraphManager.tsx:88-94.
  const nextEnabled = preferences?.enabled && preferences.enabled.length > 0
? preferences.enabled
    : Array.from(INSIGHTS_MIRROR_DEFAULT_ORDER);
  useEffect(() => {
    if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
    readDebounceRef.current = setTimeout(() => {
      setDebouncedEnabled(nextEnabled);
    }, 120);
    return () => {
      if (readDebounceRef.current) clearTimeout(readDebounceRef.current);
    };
  }, [nextEnabled]);

  // [T246.5-FE-WIRE] Derive the effective panel order. The canonical
  // mirror order is intersected with the user's enabled list. When the
  // cache is cold we fall back to the full default so the UI is
  // non-empty on the first paint.
  const effectivePanels: Set<string> = useMemo(() => {
    const enabled = debouncedEnabled ?? Array.from(INSIGHTS_MIRROR_DEFAULT_ORDER);
    return new Set(enabled);
  }, [debouncedEnabled]);

  // [T246.5-FE-WIRE] Convert the kind set back to a PanelId-ordered
  // list. The order matches the canonical mirror so the first tab the
  // user sees is always the same regardless of which kinds the user has
  // enabled.
  const effectivePanelIds: PanelId[] = useMemo(() => {
    const ids: PanelId[] = [];
    for (const kind of INSIGHTS_MIRROR_DEFAULT_ORDER) {
      const entry = Object.entries(PANEL_ID_TO_WIDGET_KIND).find(
        ([, v]) => v === kind,
      );
      if (entry && effectivePanels.has(kind)) {
        ids.push(entry[0] as PanelId);
      }
    }
    return ids;
  }, [effectivePanels]);

  // [T246.5-FE-WIRE] Dashboard variant filters to the 3 primary PanelIds;
  // full variant shows the entire enabled set (canonical order preserved).
  // The dashboard variant intentionally does NOT consult the user's
  // `enabled` list for the full set -- it pins the 3 primary tabs so the
  // compact dashboard variant always has a meaningful summary.
  const visiblePanels: PanelId[] = variant === 'dashboard'
    ? effectivePanelIds.filter((id) => DASHBOARD_PANEL_IDS.has(id))
    : effectivePanelIds;

  // Ensure activeTab is valid for current visible panels
  useEffect(() => {
    if (visiblePanels.length && !visiblePanels.includes(activeTab as PanelId)) {
      setActiveTab(visiblePanels[0]);
    }
  }, [visiblePanels, activeTab]);

  // ── Debounced persistence [T70P2-b] ──────────────────────────────────────
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistPreferences = useCallback(
    (updates: Record<string, unknown>) => {
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        dispatch(updateProfile({ preferences: updates as Record<string, string | string[]> }));
        // Also write to localStorage for immediate offline access
        if (user?.id) {
          try {
            const key = `insightsPrefs:${user.id}`;
            const existing = JSON.parse(localStorage.getItem(key) || '{}');
            localStorage.setItem(key, JSON.stringify({ ...existing, ...updates }));
          } catch { /* ignore */ }
        }
      }, 600);
    },
    [dispatch, user?.id]
  );

  // Persist tab preference changes
  useEffect(() => {
    if (variant === 'full') {
      persistPreferences({ insightsGroupBy: activeTab });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, variant]);

  // API fetch state (full variant)
  const [apiInsights, setApiInsights] = useState<InsightsState | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // [T83P2] New analytics tab data
  // [T246-MIGRATION-4] `HeatmapDay` now resolves to the insightsContracts type.
  const [heatmapData, setHeatmapData] = useState<HeatmapDay[]>([]);
  const [dowData, setDowData] = useState<DayOfWeekData[]>([]);
  // [T263-PY] Re-introduced `dowCategoryData` — the skin now consumes the
  // stacked-by-category view (one `<Bar stackId="stack">` per unique category)
  // so we fetch the BE's category-broken-down endpoint in parallel.
  const [dowCategoryData, setDowCategoryData] = useState<DayOfWeekCategoryData[]>([]);
  // [T270-PY] DayOfWeek redesign skin expects explicit loading/error/window props.
  const [dowLoading, setDowLoading] = useState(false);
  const [dowError, setDowError] = useState<string | null>(null);
  // Window envelope derived from filters (timezone-aware via `getDateRangeFromFilter`).
  const dowWindow = useMemo<{ start: string; end: string }>(() => {
    if (!filters) return { start: '', end: '' };
    const range = getDateRangeFromFilter(filters.period, filters.dateFrom, filters.dateTo, timezone);
    return { start: range.dateFrom ?? '', end: range.dateTo ?? '' };
  }, [filters, timezone]);
  const dowWindowStart = dowWindow.start;
  const dowWindowEnd = dowWindow.end;
  // [T270-PY] Stable re-fetch callback surfaced as `onRetry` to DayOfWeekRedesignSkin.
  const refetchDow = useCallback(async () => {
    if (variant !== 'full') return;
    const params = buildAnalyticsParams(filters ?? { period: '30d' }, timezone);
    setDowLoading(true);
    setDowError(null);
    let completed = 0;
    const total = 2;
    const finish = (err?: unknown) => {
      if (err) {
        setDowError('Failed to load day-of-week data. Please try again.');
        setDowLoading(false);
        return;
      }
      completed += 1;
      if (completed >= total) setDowLoading(false);
    };
    try {
      const flat = await api.get(endpoints.transactions.dayofweek, { params });
      setDowData(flat.data?.data ?? []);
      finish();
    } catch (e) {
      finish(e);
    }
    try {
      const cat = await api.get(endpoints.transactions.dayofweek, { params: { ...params, byCategory: 'true' } });
      setDowCategoryData(cat.data?.data ?? []);
      finish();
    } catch (e) {
      finish(e);
    }
  }, [variant, filters, timezone]);
  const [merchantData, setMerchantData] = useState<MerchantRow[]>([]);
  const [histogramData, setHistogramData] = useState<HistogramBucket[]>([]);
  // [T83P2-b] Net cash flow waterfall data
  const [cashFlowData, setCashFlowData] = useState<NetCashflowPoint[]>([]);
  // [T246-MIGRATION-8] The legacy `CashFlowGroupBy` ('month' | 'category' |
  // 'account') union was removed along with CashFlowWaterfallTab — the
  // canonical `cashflow-composed` skin drives its own grouping internally.
  // The state is preserved here (typed as `string`) so the legacy
  // `groupBy: cashFlowGroupBy` query-param on
  // /transactions/analytics/net-cashflow keeps working without a contract
  // break, but the setter is no longer surfaced in the UI.
  const [cashFlowGroupBy] = useState<string>('month');

  // ── Dashboard variant: compute from passed transactions array ──────────────
  const localInsights = useMemo<InsightsState>(() => {
    if (!transactions.length) return EMPTY_INSIGHTS;
    return {
      byType: buildInsightsByType(transactions),
      byCategory: buildCategoryTotals(transactions, 7),
      byAccount: buildInsightsByAccount(transactions, accounts),
      recurring: buildRecurringBreakdown(transactions),
      totalCount: transactions.length,
    };
  }, [transactions, accounts]);

  // ── Full variant: fetch aggregated data from /transactions/summary ─────────
  const fetchSummary = useCallback(async () => {
    setIsFetching(true);
    setFetchError(null);
    try {
      const params = buildAnalyticsParams(filters ?? { period: '30d' }, timezone);
      const response = await api.get(endpoints.transactions.summary, { params });
      const data = response.data?.data;
      setApiInsights(normaliseApiInsights(data, accounts, categories));
    } catch {
      setFetchError('Failed to load insights. Please try again.');
      setApiInsights(null);
    } finally {
      setIsFetching(false);
    }
  }, [accounts, categories, filters, timezone]);

  useEffect(() => {
    if (variant === 'full') {
      fetchSummary();
    }
  }, [variant, fetchSummary]);

  // [T83P2] Lazy-fetch new analytics tabs when activated
  // [T86P0] All filter dimensions are now passed to analytics endpoints
  useEffect(() => {
    if (variant !== 'full') return;
    const params = buildAnalyticsParams(filters ?? { period: '30d' }, timezone);
    if (activeTab === 'heatmap') {
      api.get(endpoints.transactions.heatmap, { params })
        .then((r) => setHeatmapData(
          // The `/transactions/analytics/heatmap` endpoint returns
          // `{ date, total, topDescriptions }` per day but no `count`.
          // The canonical `HeatmapDay` contract requires `count: number`;
          // coerce 0 for days with no transactions, 1 otherwise.
          ((r.data?.data ?? []) as Array<Omit<HeatmapDay, 'count'>>).map(
            (d) => ({ ...d, count: d.total > 0 ? 1 : 0 }),
          ),
        ))
        .catch(() => {});
    } else if (activeTab === 'dayOfWeek') {
      // [T263-PY] Fetch the flat per-day totals AND the per-day category
      // breakdown in parallel — the skin consumes both shapes to render the
      // stacked-by-category view + summary KPIs.
      // [T270-PY] Replaced inline fetches with the stable `refetchDow` callback so
      // loading/error/window state is observable through a single API.
      refetchDow();
    } else if (activeTab === 'merchants') {
      api.get(endpoints.transactions.merchantSummary, { params })
        .then((r) => setMerchantData(r.data?.data ?? []))
        .catch(() => {});
    } else if (activeTab === 'histogram') {
      api.get(endpoints.transactions.histogram, { params })
        .then((r) => setHistogramData(r.data?.data ?? []))
        .catch(() => {});
    } else if (activeTab === 'cashFlow') {
      api.get(endpoints.transactions.netCashflow, { params: { ...params, groupBy: cashFlowGroupBy } })
        .then((r) => setCashFlowData(r.data?.data ?? []))
        .catch(() => {});
    }
  }, [activeTab, variant, filters, cashFlowGroupBy, timezone, refetchDow]);

  // ── Choose active data source ──────────────────────────────────────────────
  const insights: InsightsState =
    variant === 'full' && apiInsights ? apiInsights : localInsights;

  // Trends adapters consume their own data sources at the render boundary below.

  // ── Bar data mappings ─────────────────────────────────────────────────────
  const categoryBarData = insights.byCategory.map((item) => ({
    label: item.category,
    total: item.total,
    count: item.count,
    color: item.color,
  }));

  const accountBarData = insights.byAccount.map((item) => ({
    label: item.accountName,
    total: item.total,
    count: item.count,
    color: item.color,
  }));

  // ── [T89P2] Category × Account cross-breakdown ───────────────────────────
  const categoryAccountData = useMemo<CategoryAccountItem[]>(() => {
    const expenseTxns = transactions.filter((t) => t.type === 'expense');
    if (!expenseTxns.length) return [];

    const catMap = new Map<string, {
      total: number;
      color: string;
      accounts: Map<string, { name: string; total: number; color: string }>;
    }>();

    for (const txn of expenseTxns) {
      const catName = txn.category ?? 'Uncategorized';
      if (!catMap.has(catName)) {
        const catItem = insights.byCategory.find((c) => c.category === catName);
        catMap.set(catName, {
          total: 0,
          color: catItem?.color ?? 'hsl(var(--chart-4))',
          accounts: new Map(),
        });
      }
      const entry = catMap.get(catName)!;
      entry.total += Math.max(0, Math.abs(txn.amount) - (txn.discountAmount ?? 0));

      const accId = txn.accountId;
      if (!entry.accounts.has(accId)) {
        const acc = accounts.find((a) => a.id === accId);
        entry.accounts.set(accId, {
          name: acc?.name ?? 'Unknown',
          total: 0,
          color: `hsl(var(--chart-${(entry.accounts.size % 5) + 1}))`,
        });
      }
      entry.accounts.get(accId)!.total += Math.max(0, Math.abs(txn.amount) - (txn.discountAmount ?? 0));
    }

    return [...catMap.entries()]
      .sort((a, b) => b[1].total - a[1].total)
      .slice(0, 7)
      .map(([category, { total, color, accounts: accMap }]) => ({
        category,
        categoryColor: color,
        total: Number(total.toFixed(2)),
        accounts: [...accMap.values()].sort((a, b) => b.total - a.total),
      }));
  }, [transactions, insights.byCategory, accounts]);


  // [T270-PY] Day-of-week drill-through builder. Maps the skin's discriminated
  // `DayOfWeekDrillInput` to a canonical `FilterState` and serialises via
  // `filtersToQueryString` (so `period=custom`, `dateFrom`, `dateTo`, `dow` CSV,
  // and `categories` CSV all flow through the single source of truth).
  const buildDayOfWeekDrillHref = useCallback(
    (input: DayOfWeekDrillInput): string => {
      const filter: FilterState = {
        period: 'custom',
        dateFrom: input.windowStart ? new Date(input.windowStart) : undefined,
        dateTo: input.windowEnd ? new Date(input.windowEnd) : undefined,
      };
      if (input.kind === 'topDay' || input.kind === 'cell') {
        filter.dayOfWeek = [input.dayOfWeek];
      }
      if (input.kind === 'topCategory' || input.kind === 'cell') {
        filter.categoryIds = [input.categoryId];
      }
      const qs = filtersToQueryString(filter);
      return qs ? `/transactions?${qs}` : '/transactions';
    },
    [],
  );


  // ── Render panel content by ID ────────────────────────────────────────────
  const renderPanelContent = (id: PanelId) => {
    switch (id) {
      case 'trends': {
        // [T246.5-FE-WIRE] Short-circuit when the user has disabled the
        // `spending-trend` widget in /widgets/insights. Returning null
        // keeps the corresponding `data-testid="tab-trends"` trigger
        // rendered (per AC2: tab triggers always render) while the
        // content panel collapses to an empty state.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.trends)) return null;
        // [T-TRENDS-V2-WIRE] Keep the compact dashboard renderer isolated from
        // the full-page adapter's API/cache lifecycle.
        if (variant === 'dashboard') {
          return (
            <div data-testid="insights-trends-tab" className="overflow-hidden min-w-0">
              <TrendsAdapter transactions={transactions} />
            </div>
          );
        }
        return (
          <div data-testid="insights-trends-tab" className="overflow-hidden min-w-0">
            <TrendsV2Adapter
              filterState={activeFilters}
              timezone={timezone}
              renderPayload={renderTrendsV2Payload}
            />
          </div>
        );
      }
      case 'byType': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.byType)) return null;
        // [T246-MIGRATION-1] Delegate to the canonical `tx-by-type-donut` widget
        // skin via the SkinProps adapter pattern. The local `InsightByType[]`
        // is coerced on-the-fly into the canonical `FacetBreakdownPayload`
        // contract the skin expects. The descriptor carries
        // `kind: 'tx-by-type-donut'` so the parity renderer routes to the
        // correct skin, and the wrapper preserves the existing E2E testid
        // `data-testid="insights-type-tab"`.
        const byTypeDescriptor: IWidget = {
          id: 'insights-byType',
          type: 'donut',
          kind: 'tx-by-type-donut',
          title: 'Transactions by Type',
          metric: 'txByType',
          x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
          dataSource: 'analytics',
          config: { groupBy: 'type' },
        };
        const byTypeData: FacetBreakdownPayload = {
          slices: insights.byType.map((t) => ({
            id: t.type,
            name: t.type,
            amount: t.total,
            count: t.count,
            percentage: t.percentage,
          })),
          total: insights.totalCount,
          groupBy: 'type',
        };
        return (
          <div data-testid="insights-type-tab" className="overflow-hidden min-w-0">
            <TxByTypeDonutSkin
              widget={byTypeDescriptor}
              kind="tx-by-type-donut"
              data={byTypeData}
              loading={false}
            />
          </div>
        );
      }
      case 'breakdown':
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.breakdown)) return null;
        return (
          <BreakdownTab
            categoryData={categoryBarData}
            accountData={accountBarData}
            categoryAccountData={categoryAccountData}
          />
        );
      case 'recurring': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.recurring)) return null;
        // [T246-MIGRATION-9] Delegate to the canonical `recurring-vs-discretionary`
        // widget skin. The local `RecurringBreakdown` is coerced on-the-fly into
        // the canonical `RecurringVsDiscretionaryPayload` (recurring / oneOff /
        // totals envelopes). The descriptor carries
        // `kind: 'recurring-vs-discretionary'` so the parity renderer routes to
        // the correct skin, and the wrapper preserves the existing E2E testid
        // `data-testid="insights-recurring-tab"`.
        const recurringDescriptor: IWidget = {
          id: 'insights-recurring',
          type: 'bar',
          kind: 'recurring-vs-discretionary',
          title: 'Recurring vs One-time',
          metric: 'recurringVsOneOff',
          x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
          dataSource: 'analytics',
          config: {},
        };
        const recurringPayload: RecurringVsDiscretionaryPayload = {
          recurring: {
            total: insights.recurring?.recurring.total ?? 0,
            count: insights.recurring?.recurring.count ?? 0,
            percentage: insights.recurring?.recurring.percentage ?? 0,
          },
          oneOff: {
            total: insights.recurring?.oneTime.total ?? 0,
            count: insights.recurring?.oneTime.count ?? 0,
            percentage: insights.recurring?.oneTime.percentage ?? 0,
          },
          totals: {
            total:
              (insights.recurring?.recurring.total ?? 0) +
              (insights.recurring?.oneTime.total ?? 0),
            count:
              (insights.recurring?.recurring.count ?? 0) +
              (insights.recurring?.oneTime.count ?? 0),
          },
          window: {
            startDate:
              (typeof filters?.dateFrom === 'string'
                ? filters.dateFrom
                : filters?.dateFrom
                  ? new Date(filters.dateFrom).toISOString().slice(0, 10)
                  : ''),
            endDate:
              (typeof filters?.dateTo === 'string'
                ? filters.dateTo
                : filters?.dateTo
                  ? new Date(filters.dateTo).toISOString().slice(0, 10)
                  : ''),
          },
        };
        return (
          <div data-testid="insights-recurring-tab" className="overflow-hidden min-w-0">
            <RecurringVsDiscretionarySkin
              widget={recurringDescriptor}
              kind="recurring-vs-discretionary"
              data={recurringPayload}
              loading={false}
            />
          </div>
        );
      }
      case 'heatmap': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.heatmap)) return null;
        // [T246-MIGRATION-4] Delegate to the canonical `spending-heatmap-calendar`
        // widget skin. The local heatmap data (fetched from
        // /transactions/analytics/heatmap in full variant) is coerced on-the-fly
        // into the canonical `HeatmapPayload` contract the skin expects — no
        // resolver round-trip is needed and the existing filter-driven data flow
        // remains intact. The `window` is derived from the active filter range
        // (defaulting to the last 84 days when no filter is set, matching the
        // backend `computeSpendingHeatmap` default).
        const heatmapDescriptor: IWidget = {
          id: 'insights-heatmap',
          type: 'heatmap',
          kind: 'spending-heatmap-calendar',
          title: 'Spending Calendar',
          metric: 'spendingHeatmap',
          x: 0,
          y: 0,
          w: 6,
          h: 3,
          minW: 4,
          minH: 2,
          dataSource: 'analytics',
          config: {},
        };
        // Derive window from filters (or default to last 84 days).
        const dateRange = getDateRangeFromFilter(
          filters?.period ?? '30d',
          filters?.dateFrom,
          filters?.dateTo,
          timezone,
        );
        const windowEnd = dateRange.dateTo
          ?? new Date().toISOString().slice(0, 10);
        const windowStart = dateRange.dateFrom
          ?? new Date(Date.now() - 83 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        const heatmapData_payload: HeatmapPayload = {
          days: heatmapData,
          window: { startDate: windowStart, endDate: windowEnd },
          totals: {
            sum: heatmapData.reduce((s, d) => s + d.total, 0),
            count: heatmapData.filter((d) => d.total > 0).length,
          },
        };
        return (
          <div data-testid="insights-heatmap-tab" className="overflow-hidden min-w-0">
            <SpendingHeatmapCalendarSkin
              widget={heatmapDescriptor}
              kind="spending-heatmap-calendar"
              data={heatmapData_payload}
              loading={false}
              onViewTransactions={onCalendarDayFilter}
            />
          </div>
        );
      }
      case 'dayOfWeek': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.dayOfWeek)) return null;
        // [T270-PY] Delegated to the redesigned `DayOfWeekRedesignSkin`
        // (./DayOfWeekRedesignSkin). The legacy `day-of-week-bar` descriptor stays in
        // `widgetKindRegistry.ts` for `/widgets/insights` parity; the testid
        // `data-testid="insights-day-of-week-tab"` is preserved by the JSX below.
        // Adapter: local `{ day, total, count }` → canonical
        // `{ dayOfWeek, dayName, totalSpent, averageSpent, count }`.
        const DOW_INDEX_BY_NAME: Record<string, number> = {
          Sunday: 1, Monday: 2, Tuesday: 3, Wednesday: 4, Thursday: 5, Friday: 6, Saturday: 7,
          Sun: 1, Mon: 2, Tue: 3, Wed: 4, Thu: 5, Fri: 6, Sat: 7,
        };
        // [T263-PY] Build the per-day adapter from BOTH `dowData` (flat totals) and
        // `dowCategoryData` (per-day category breakdown from byCategory=true).
        // Merge them by day-name so the skin gets a fully-populated
        // `DayOfWeekPayload`.
        const categoryByDay = new Map<string, DayOfWeekCategoryData['categories']>();
        for (const cd of dowCategoryData) {
          categoryByDay.set(cd.day, cd.categories ?? []);
        }
        const adapterDays = dowData.map((d, idx) => {
          const cats = categoryByDay.get(d.day) ?? [];
          return {
            dayOfWeek: DOW_INDEX_BY_NAME[d.day] ?? idx + 1,
            dayName: d.day,
            totalSpent: d.total,
            averageSpent: d.count > 0 ? d.total / d.count : 0,
            count: d.count,
            // Cast categoryId → string (BE returns `string | null`, contract
            // expects string). Synthetic `Uncategorized` rows keep their
            // `null` id coerced to '' so the skin's color lookup by id
            // remains stable.
            categories: cats.map((c) => ({
              categoryId: c.categoryId ?? '',
              name: c.name,
              emoji: c.emoji,
              total: c.total,
              count: c.count,
            })),
          };
        });
        const adapterTotal = adapterDays.reduce((s, d) => s + d.totalSpent, 0);
        const adapterAverage = adapterDays.length > 0 ? adapterTotal / adapterDays.length : 0;
        // [T264-PY] BE /transactions/analytics/dayofweek returns `day` as the
        // 3-letter abbreviation (`'Mon'..'Sun'`); the previous filter compared
        // against the full names `'Saturday'`/`'Sunday'`, so `isWeekend` was
        // always false → `weekendAverage = 0` and `weekdayAverage` collapsed
        // to the 7-day mean. Use the abbreviations the adapter actually
        // receives (line 1130 maps `d.day` → `dayName`).
        const isWeekend = (d: { dayName: string }) => d.dayName === 'Sat' || d.dayName === 'Sun';
        const wkDays = adapterDays.filter((d) => !isWeekend(d));
        const weDays = adapterDays.filter(isWeekend);
        const wkAvg = wkDays.length > 0 ? wkDays.reduce((s, d) => s + d.totalSpent, 0) / wkDays.length : 0;
        const weAvg = weDays.length > 0 ? weDays.reduce((s, d) => s + d.totalSpent, 0) / weDays.length : 0;
        const adapterHighest = adapterDays.reduce<(typeof adapterDays)[number] | null>(
          (acc, d) => (d.totalSpent > 0 && (!acc || d.totalSpent > acc.totalSpent) ? d : acc),
          null,
        );
        // Aggregate categories across all days for `highestCategory`.
        const catTotals = new Map<string, { name: string; emoji: string; total: number }>();
        for (const d of adapterDays) {
          for (const c of d.categories) {
            const key = c.categoryId || c.name;
            const prev = catTotals.get(key);
            if (prev) {
              prev.total += c.total;
            } else {
              catTotals.set(key, { name: c.name, emoji: c.emoji, total: c.total });
            }
          }
        }
        const topCategoryEntry = Array.from(catTotals.entries())
          .map(([id, v]) => ({ categoryId: id, ...v }))
          .sort((a, b) => b.total - a.total)[0];
        const dayOfWeekPayload: DayOfWeekPayload = {
          days: adapterDays,
          average: adapterAverage,
          weekdayAverage: wkAvg,
          weekendAverage: weAvg,
          highestDay: adapterHighest
            ? { dayOfWeek: adapterHighest.dayOfWeek, dayName: adapterHighest.dayName, totalSpent: adapterHighest.totalSpent }
            : null,
          highestCategory: topCategoryEntry
            ? { categoryId: topCategoryEntry.categoryId, name: topCategoryEntry.name, emoji: topCategoryEntry.emoji, total: topCategoryEntry.total }
            : null,
        };
        return (
          <div data-testid="insights-day-of-week-tab" className="overflow-hidden min-w-0">
            <DayOfWeekRedesignSkin
              data={dayOfWeekPayload}
              loading={dowLoading}
              error={dowError}
              onRetry={refetchDow}
              windowStart={dowWindowStart}
              windowEnd={dowWindowEnd}
              today={new Date()}
              buildDrillHref={isFeatureEnabled('day-of-week-server-filter') ? buildDayOfWeekDrillHref : undefined}
            />
          </div>
        );
      }
      case 'merchants': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.merchants)) return null;
        // [T246-MIGRATION-6] Delegate to the canonical `top-merchants-bar` widget
        // skin. The local `MerchantRow[]` is coerced on-the-fly into the
        // canonical `TopMerchantsPayload` (`merchants: [{name, totalSpent, count,
        // percentage}]` + `total`) the skin expects. The descriptor carries
        // `kind: 'top-merchants-bar'` so the parity renderer routes to the
        // correct skin, and the wrapper preserves the existing E2E testid
        // `data-testid="insights-merchants-tab"`.
        const merchantsDescriptor: IWidget = {
          id: 'insights-merchants',
          type: 'bar',
          kind: 'top-merchants-bar',
          title: 'Top Merchants',
          metric: 'topMerchantsList',
          x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
          dataSource: 'analytics',
          config: {},
        };
        const merchantTotal = merchantData.reduce((s, m) => s + m.total, 0);
        const merchantDenom = merchantTotal || 1;
        const merchantsPayload: TopMerchantsPayload = {
          merchants: merchantData.map((m) => ({
            name: m.name,
            totalSpent: m.total,
            count: m.count,
            percentage: (m.total / merchantDenom) * 100,
          })),
          total: merchantTotal,
        };
        return (
          <div data-testid="insights-merchants-tab" className="overflow-hidden min-w-0">
            <TopMerchantsBarSkin
              widget={merchantsDescriptor}
              kind="top-merchants-bar"
              data={merchantsPayload}
              loading={false}
            />
          </div>
        );
      }
      case 'histogram': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.histogram)) return null;
        // [T246-MIGRATION-7] Delegate to the canonical `amount-histogram-bar`
        // widget skin. The local `HistogramBucket[]` (label, min, max, count)
        // is coerced on-the-fly into the canonical `AmountHistogramPayload`
        // (`buckets: [{label, min, max, count, total}]` + `totalCount`) the
        // skin expects. The local buckets lack a `total` field, so we default
        // to 0 (the skin only reads `total` inside the tooltip). The
        // descriptor carries `kind: 'amount-histogram-bar'` so the parity
        // renderer routes to the correct skin, and the wrapper preserves the
        // existing E2E testid `data-testid="insights-histogram-tab"`.
        const histogramDescriptor: IWidget = {
          id: 'insights-histogram',
          type: 'bar',
          kind: 'amount-histogram-bar',
          title: 'Transaction Size Distribution',
          metric: 'insightsAmountHistogram',
          x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
          dataSource: 'analytics',
          config: {},
        };
        const histogramPayload: AmountHistogramPayload = {
          buckets: histogramData.map((b) => ({
            label: b.label,
            min: b.min,
            max: b.max,
            count: b.count,
            total: 0, // local API omits per-bucket total; tooltip degrades gracefully
          })),
          totalCount: histogramData.reduce((s, b) => s + b.count, 0),
          window: {
            startDate:
              (typeof filters?.dateFrom === 'string'
                ? filters.dateFrom
                : filters?.dateFrom
                  ? new Date(filters.dateFrom).toISOString().slice(0, 10)
                  : ''),
            endDate:
              (typeof filters?.dateTo === 'string'
                ? filters.dateTo
                : filters?.dateTo
                  ? new Date(filters.dateTo).toISOString().slice(0, 10)
                  : ''),
          },
        };
        return (
          <div data-testid="insights-histogram-tab" className="overflow-hidden min-w-0">
            <AmountHistogramBarSkin
              widget={histogramDescriptor}
              kind="amount-histogram-bar"
              data={histogramPayload}
              loading={false}
            />
          </div>
        );
      }
      case 'cashFlow': {
        // [T246.5-FE-WIRE] Effective-panel guard -- see AC2 / 7a.
        if (!effectivePanels.has(PANEL_ID_TO_WIDGET_KIND.cashFlow)) return null;
        // [T246-MIGRATION-8] Delegate to the canonical `cashflow-composed`
        // widget skin. The local `NetCashflowPoint[]` (label, income,
        // expenses, net) is coerced on-the-fly into the canonical
        // `NetCashflowPayload` (`points: [{date, income, expense, net}]`).
        // Field rename: `label` → `date`, `expenses` → `expense`. The
        // descriptor carries `kind: 'cashflow-composed'` so the parity
        // renderer routes to the correct skin, and the wrapper preserves the
        // existing E2E testid `data-testid="insights-cash-flow-tab"`.
        // NOTE: The legacy `cashFlowGroupBy` / `setCashFlowGroupBy` UI toggle
        // is dropped here — the canonical skin renders a single composed
        // view driven by the underlying `cashflow-composed` resolver. The
        // The `cashFlowGroupBy` state remains in scope at the hook level
        // (passed to the legacy `groupBy` query-param on
        // `/transactions/analytics/net-cashflow`); the setter is no longer
        // surfaced in the UI because the canonical `cashflow-composed` skin
        // handles grouping internally.
        const cashFlowDescriptor: IWidget = {
          id: 'insights-cashFlow',
          type: 'line',
          kind: 'cashflow-composed',
          title: 'Cash Flow',
          metric: 'dailyCashflow',
          x: 0, y: 0, w: 6, h: 4, minW: 3, minH: 3,
          dataSource: 'analytics',
          config: {},
        };
        const cashFlowPayload: NetCashflowPayload = {
          points: cashFlowData.map((p) => ({
            date: p.label,
            income: p.income,
            expense: p.expenses,
            net: p.net,
          })),
        };
        return (
          <div data-testid="insights-cash-flow-tab" className="overflow-hidden min-w-0">
            <CashflowComposedSkin
              widget={cashFlowDescriptor}
              kind="cashflow-composed"
              data={cashFlowPayload}
              loading={false}
            />
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <Card
      className="transition-all duration-200 hover:shadow-md hover:border-primary/20 min-w-0 overflow-hidden"
      data-testid="transaction-insights-card"
      data-section="transaction-insights"
      data-skin-id="tabs-insights"
    >
      <CardHeader className="flex flex-row items-start justify-between gap-2 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="p-2 bg-primary/10 rounded-full shrink-0">
            <BarChart3 className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <CardTitle className="truncate">Transaction Insights</CardTitle>
            <p className="text-sm text-muted-foreground truncate">
              {variant === 'dashboard'
                ? 'Based on recent transactions'
                : 'Grouped breakdown of your transactions'}
            </p>
          </div>
        </div>

        {/* [T262-PY] Customize entry point. Opens the quick-curation modal
            (CustomizeInsightsDialog) so users can toggle individual widgets
            without leaving the page. testid contract preserved from T260-PY. */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="shrink-0 size-11 min-h-[44px] min-w-[44px]"
          onClick={() => setCustomizeOpen(true)}
          data-testid="insights-customize-trigger"
          aria-label="Customize insights widgets"
          aria-haspopup="dialog"
        >
          <SlidersHorizontal className="size-5" aria-hidden="true" />
        </Button>

      </CardHeader>

      <CardContent className="overflow-hidden px-3 sm:px-6">
        {/* Error state */}
        {variant === 'full' && fetchError && (
          <div className="flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive mb-3">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {fetchError}
          </div>
        )}

        {/* Loading skeleton */}
        {variant === 'full' && isFetching ? (
          <div className="py-10 flex flex-col items-center gap-3 text-muted-foreground text-sm">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading insights…
          </div>
        ) : visiblePanels.length === 0 ? (
          /* [T246.5-FE-WIRE] Empty-state CTA -- when the user has disabled
             every widget in /widgets/insights, surface a single hand-off
             link to the curation page instead of an empty Tabs shell.
             The `data-testid="insights-empty-state"` hook lets the test
             suite assert the empty state renders deterministically. */
          <div
            data-testid="insights-empty-state"
            className="py-10 flex flex-col items-center gap-3 text-muted-foreground text-sm"
          >
            <p>No insights enabled. Visit /widgets/insights to enable some.</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/widgets/insights" data-testid="insights-empty-state-cta">
                Open Insights Manager
              </Link>
            </Button>
          </div>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList
              ref={tabsListRef}
              className="relative mb-4 flex justify-start h-10 min-h-[44px] w-full min-w-0 gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain scrollbar-none rounded-lg bg-muted p-1 text-muted-foreground scroll-mt-[120px]"
              data-testid="insights-tabs"
            >
              {visiblePanels.map((id, idx) => (
                <TabsTrigger
                  key={id}
                  value={id}
                  onClick={() => {
                    if (id === 'breakdown') requestAnimationFrame(resetTabStrip);
                  }}
                  className="text-xs sm:text-sm gap-1 shrink-0 scroll-mt-[220px] min-h-[44px] min-w-[44px]"
                  data-testid={`tab-${id}`}
                >
                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4 leading-none font-normal">
                    {idx + 1}
                  </Badge>
                  {id === 'trends' && <TrendingUp className="h-3.5 w-3.5" />}
                  {PANEL_LABELS[id]}
                </TabsTrigger>
              ))}
            </TabsList>

            {visiblePanels.map((id) => (
              <TabsContent key={id} value={id} className="mt-0 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-200">
                {renderPanelContent(id)}
              </TabsContent>
            ))}
          </Tabs>
        )}
      </CardContent>
      {/* [T262-PY] Quick-curation modal. Sits inside the Card tree but Radix
          Dialog portals its content to <body>, so it visually overlays the page
          rather than being constrained by the Card's box. */}
      <CustomizeInsightsDialog
        open={customizeOpen}
        onOpenChange={setCustomizeOpen}
      />
    </Card>
  );
}
